import React, { useCallback, useEffect, useMemo } from 'react';
import NumberFormat from 'react-number-format';
import { useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { getBooleanField, getTextField } from '@lender/utils';
import { Box, Typography } from '@mui/material';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import {
  AutocompleteCDS,
  CheckboxCDS,
  TextFieldCDS,
} from 'app/shared/componentsRestyled/coreDesignSystem/form';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { object } from 'yup';

import { FeatureFlag } from 'constants/featureFlags';
import {
  ActionNameType,
  CheckboxRenderType,
  DYNAMIC_FIELD_DEFAULT_MULTIPLIER,
  DYNAMIC_FIELDS_WITH_MULTIPLIER,
  DynamicFieldPostfixSymbol,
  DynamicFieldPrefixSymbol,
  FormDateFormat,
  FormSchemaType,
  NUMERIC_FIELDS_WITH_DECIMAL_POSITIONS,
  NUMERIC_FIELDS_WITH_THOUSANDS_SEPARATOR,
  PDF_VERTICAL_ALIGNMENT_EXCLUDED_TYPES,
  TAG_ASSIGNED_DATE_DYNAMIC_FIELD,
  TAG_NAME_DYNAMIC_FIELD,
} from 'constants/forms';
import {
  defaultDynamicFieldPostfixOption,
  defaultDynamicFieldPrefixOption,
  dynamicFieldPostfixOptions,
  dynamicFieldPrefixOptions,
  floatOptions,
} from 'constants/options';
import useForm from 'hooks/useForm';
import { getConditionalObject } from 'utils/data';
import {
  checkIsTagAssignedDate,
  checkIsTagName,
  getActionProperty,
  getDecimalPositionsFromAction,
  getFieldDecimalPositionsDefault,
  getWithThousandsSeparatorFromAction,
  isDecimalPositionsAllowed,
  isMultiplierAllowed,
  isPdfFormType,
} from 'utils/forms';

import { selectSystemTagsValues } from 'store/system/selectors';

import { formsTestIds } from '../../forms.testIds';

import DynamicFieldBoldCheckbox from './DynamicFieldBoldCheckbox';
import { fullWidthCellStyles, sectionHeaderStyles } from './DynamicFieldOptions.styles';
import {
  findOptionByValue,
  getInitialHorizontalAlignment,
  getInitialVerticalAlignment,
} from './DynamicFieldOptions.utils';
import DynamicFieldOptionsLayout from './DynamicFieldOptionsLayout';
import StyleViewOptions from './StyleViewOptions';

const getSchema = ({ isTag, isLabelRequired }) =>
  object().shape({
    isBold: getBooleanField({ isRequired: false }),
    isUppercase: getBooleanField({ isRequired: false }),
    fontSize: getTextField({ isRequired: false }),
    fontColor: getTextField({ isRequired: false }),
    fontFamily: getTextField({ isRequired: false }),
    checkboxRenderType: getTextField({ isRequired: false }),
    horizontalAlignment: getTextField({ isRequired: false }),
    verticalAlignment: getTextField({ isRequired: false }),
    tag: getTextField({ isRequired: isTag }).nullable(),
    labelText: getTextField({ isRequired: isLabelRequired }),
  });

const DynamicFieldStyleOptionsView = ({
  setParentView,
  chosenOption,
  handleClose,
  style,
  checkboxRenderType = '',
  action,
  editor,
  onSave,
  withLabel: initialWithLabel,
  poaVerbiage: initialPoaVerbiage,
  dataDynamicPostfix,
  dataDynamicPrefix,
  dataDynamicMultiplier,
  isAvailablePostfix,
  isAvailablePrefix,
  isAvailableSpelledOut = false,
  schemaDefaultAction,
  fontFamilyOptions,
  defaultFontFamilyOption,
  isNewItem = false,
}) => {
  const [title, value, type, label] = chosenOption;
  const tags = useSelector(selectSystemTagsValues);
  const isBackgroundColorEnabled = useFeatureToggle(FeatureFlag.EnableDynamicFieldBackgroundColor);

  const isPdfForm = isPdfFormType(editor.formType);
  const isCheckboxType = type === FormSchemaType.BOOL;
  const isPercentType = type === FormSchemaType.PERCENT;
  const hasPercentPostfix = isAvailablePostfix || isPercentType;
  const hasDollarPrefix = isAvailablePrefix || isPercentType;
  const isDateType = type === FormSchemaType.DATE;
  const isStringType = type === FormSchemaType.STRING;
  const isImageType = type === FormSchemaType.IMAGE;
  const isBlankSignatureLine = type === FormSchemaType.BLANK_SIGNATURE_LINE;
  const isVerticalAlignmentExcluded = PDF_VERTICAL_ALIGNMENT_EXCLUDED_TYPES.includes(type);
  const isBackgroundColorAvailable =
    isPdfForm &&
    isBackgroundColorEnabled &&
    !isCheckboxType &&
    !isImageType &&
    !isBlankSignatureLine;
  const isNumericWithDecimalPositions = NUMERIC_FIELDS_WITH_DECIMAL_POSITIONS.includes(type);
  const isNumericWithThousandsSeparator = NUMERIC_FIELDS_WITH_THOUSANDS_SEPARATOR.includes(type);
  const isMultiplierVisible = DYNAMIC_FIELDS_WITH_MULTIPLIER.includes(type);

  const isTagName = checkIsTagName(title);
  const isTagAssignedDate = checkIsTagAssignedDate(title);
  const isTag = isTagName || isTagAssignedDate;

  const [includeSign] = action?.args || [];
  const initialDecimalPositions = isNumericWithDecimalPositions
    ? getDecimalPositionsFromAction(action, type, schemaDefaultAction)
    : null;
  const initialWithThousandsSeparator = isNumericWithThousandsSeparator
    ? getWithThousandsSeparatorFromAction(action, type, schemaDefaultAction)
    : null;

  const initialDisplayPercent =
    ([dataDynamicPrefix, includeSign].every(_.isUndefined) && isPercentType) ||
    dataDynamicPostfix === DynamicFieldPostfixSymbol.PERCENT ||
    (isPercentType && !!includeSign);
  const initialDisplayDollar = dataDynamicPrefix === DynamicFieldPrefixSymbol.DOLLAR;

  const initialPostfix = initialDisplayPercent
    ? findOptionByValue(dynamicFieldPostfixOptions, DynamicFieldPostfixSymbol.PERCENT)
    : defaultDynamicFieldPostfixOption;
  const initialPrefix = initialDisplayDollar
    ? findOptionByValue(dynamicFieldPrefixOptions, DynamicFieldPrefixSymbol.DOLLAR)
    : defaultDynamicFieldPrefixOption;

  const selectedTag = useMemo(() => tags.find(({ tagId }) => value.includes(tagId)), [tags, value]);
  const isDateOnlyInitialValue = action?.args?.[0] === FormDateFormat.DATE_ONLY ? true : false;
  const isInvertedBoolValue = action?.name === ActionNameType.INVERSE_BOOL_VALUE;
  const isSpelledOutInitialValue = action?.name === ActionNameType.STATE_SPELLED_OUT;

  const { form, setSchema, handleChange, setInForm, validateField, validationErrors } = useForm({
    postfix: hasPercentPostfix ? initialPostfix : undefined,
    prefix: hasDollarPrefix ? initialPrefix : undefined,
    includeSign,
    decimalPositions: initialDecimalPositions,
    withThousandsSeparator: initialWithThousandsSeparator,
    multiplier: isMultiplierVisible
      ? (dataDynamicMultiplier ?? DYNAMIC_FIELD_DEFAULT_MULTIPLIER)
      : undefined,
    horizontalAlignment:
      isPdfForm && !isCheckboxType
        ? getInitialHorizontalAlignment({ align: style?.align, isNewItem })
        : undefined,
    verticalAlignment:
      isPdfForm && !isVerticalAlignmentExcluded
        ? getInitialVerticalAlignment({ verticalAlign: style?.verticalAlign })
        : undefined,
    isBold: !!style?.isBold,
    isUppercase: !!style?.isUppercase,
    fontColor: style?.fontColor,
    backgroundColor: isPdfForm ? style?.backgroundColor || null : null,
    fontSize: { value: style?.fontSize },
    fontFamily: { value: style?.fontFamily },
    checkboxRenderType: isCheckboxType
      ? checkboxRenderType || CheckboxRenderType.Checkbox.value
      : undefined,
    tag: selectedTag,
    isDateOnly: isDateOnlyInitialValue,
    checkboxInverseValue: isInvertedBoolValue,
    isSpelledOut: isSpelledOutInitialValue,

    ...getConditionalObject(isCheckboxType, {
      withLabel: initialWithLabel,
      labelText: label,
    }),

    ...getConditionalObject(isBlankSignatureLine, {
      poaVerbiage: initialPoaVerbiage,
    }),

    ...getConditionalObject(isImageType, () => ({
      float: floatOptions.find(({ value: floatValue }) => floatValue === style?.float),
      size: style?.size,
    })),
  });

  const isSubmitDisabled = isTag && !form.tag;
  const isLabelRequired = isCheckboxType && !!form.withLabel;

  useEffect(() => {
    setSchema(getSchema({ isTag, isLabelRequired }));
  }, [setSchema, isTag, isLabelRequired]);

  useEffect(() => {
    if (!form.withLabel) {
      setInForm('labelText', label);
    }
  }, [form.withLabel, setInForm, label]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      const getFieldTitle = () => {
        if (isTagName) {
          return `${form.tag.name} - ${TAG_NAME_DYNAMIC_FIELD}`;
        }

        if (isTagAssignedDate) {
          return `${form.tag.name} - ${TAG_ASSIGNED_DATE_DYNAMIC_FIELD}`;
        }

        return title;
      };

      const fieldTitle = getFieldTitle();

      const getFieldValue = () => {
        if (isTagName) {
          return form.tag.tagId;
        }

        if (isTagAssignedDate) {
          return `${form.tag.tagId}_assignedDate`;
        }

        return value;
      };

      const fieldValue = getFieldValue();

      const action = getActionProperty({
        dataDynamicType: type,
        includeSign: isPercentType ? false : form.includeSign,
        ...getConditionalObject(isNumericWithDecimalPositions, () => ({
          decimalPositions:
            form.decimalPositions === '' || _.isNil(form.decimalPositions)
              ? getFieldDecimalPositionsDefault(schemaDefaultAction, type)
              : Number(form.decimalPositions),
        })),
        ...getConditionalObject(isNumericWithThousandsSeparator, () => ({
          withThousandsSeparator: form.withThousandsSeparator,
        })),
        isDateOnly: form.isDateOnly,
        checkboxInverseValue: form.checkboxInverseValue,
        isSpelledOut: form.isSpelledOut,
      });

      const dynamicPostfix = form.postfix?.value || undefined;
      const dynamicPrefix = form.prefix?.value || undefined;

      onSave({
        title: fieldTitle,
        value: fieldValue,
        type,
        label: isCheckboxType ? form.labelText : label,
        withLabel: isCheckboxType ? form.withLabel : undefined,
        action,
        dataDynamicPostfix: dynamicPostfix,
        dataDynamicPrefix: dynamicPrefix,
        dataDynamicMultiplier:
          form.multiplier === '' || _.isNil(form.multiplier)
            ? DYNAMIC_FIELD_DEFAULT_MULTIPLIER
            : form.multiplier,
        style: {
          isBold: form.isBold,
          isUppercase: form.isUppercase,
          fontColor: form.fontColor,
          fontSize: form.fontSize?.value,
          fontFamily: form.fontFamily?.value,
          ...getConditionalObject(isPdfForm, () => ({
            checkboxRenderType: form.checkboxRenderType,
            align: form.horizontalAlignment?.value,
            verticalAlign: form.verticalAlignment?.value,
            backgroundColor: form.backgroundColor || null,
          })),
          ...getConditionalObject(isImageType, {
            float: form.float?.value,
            size: form.size,
            display: 'inline-block',
          }),
        },
        checkboxRenderType: !isPdfForm ? form.checkboxRenderType : undefined,
        checkboxInverseValue: form.checkboxInverseValue,
        poaVerbiage: form.poaVerbiage,
        isTag,
      });
    },
    [
      type,
      form,
      onSave,
      title,
      value,
      label,
      isPdfForm,
      isTagName,
      isTagAssignedDate,
      isImageType,
      isCheckboxType,
      isTag,
      isPercentType,
      isNumericWithDecimalPositions,
      isNumericWithThousandsSeparator,
      schemaDefaultAction,
    ],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const onMultiplierBlur = useCallback(() => {
    if (form.multiplier !== '' && !_.isNil(form.multiplier)) {
      return;
    }

    setInForm('multiplier', DYNAMIC_FIELD_DEFAULT_MULTIPLIER);
  }, [form.multiplier, setInForm]);

  const onDecimalPositionsBlur = useCallback(() => {
    if (form.decimalPositions !== '' && !_.isNil(form.decimalPositions)) {
      return;
    }

    setInForm('decimalPositions', getFieldDecimalPositionsDefault(schemaDefaultAction, type));
  }, [form.decimalPositions, setInForm, type, schemaDefaultAction]);

  const onGetTagOptionSelected = useCallback((option, value) => option.tagId === value.tagId, []);

  const showStyleSection = !isImageType;
  const isBoldInFirstRow = showStyleSection && !isCheckboxType;
  const hasLeadingCheckbox = isStringType || isDateType || isBlankSignatureLine;

  return (
    <DynamicFieldOptionsLayout
      chosenOption={chosenOption}
      dataTestId={formsTestIds.DYNAMIC_FIELD_STYLE_OPTIONS_VIEW}
      editor={editor}
      isSubmitDisabled={isSubmitDisabled}
      setParentView={setParentView}
      onClose={handleClose}
      onSubmit={onSubmit}
    >
      <Box sx={fullWidthCellStyles}>
        <Typography color="text.secondary" sx={sectionHeaderStyles} variant="bold12">
          {isImageType ? 'Display Options' : 'Display Options & Formatting'}
        </Typography>
      </Box>

      {isCheckboxType && (
        <>
          <CheckboxCDS
            fullWidth
            dataTestId={formsTestIds.STYLE_OPTIONS_INVERSE_VALUE_CHECKBOX}
            label="Inverse value"
            name="checkboxInverseValue"
            value={form.checkboxInverseValue}
            onChange={handleChange}
          />

          <CheckboxCDS
            fullWidth
            dataTestId={formsTestIds.STYLE_OPTIONS_WITH_LABEL_CHECKBOX}
            label="With label"
            name="withLabel"
            value={form.withLabel}
            onChange={handleChange}
          />

          <TextFieldCDS
            fullWidth
            required
            columns={2}
            dataTestId={formsTestIds.STYLE_OPTIONS_LABEL_INPUT}
            error={!!validationErrors.labelText}
            helperText={validationErrors.labelText}
            isHidden={!form.withLabel}
            label="Label"
            name="labelText"
            type="text"
            value={form.labelText}
            variant="outlined"
            onChange={handleChange}
          />
        </>
      )}

      {isDateType && (
        <CheckboxCDS
          fullWidth
          dataTestId={formsTestIds.STYLE_OPTIONS_DATE_ONLY_CHECKBOX}
          label="Date Only"
          name="isDateOnly"
          value={form.isDateOnly}
          onChange={handleChange}
        />
      )}

      {isBlankSignatureLine && (
        <CheckboxCDS
          fullWidth
          dataTestId={formsTestIds.STYLE_OPTIONS_POA_VERBIAGE_CHECKBOX}
          label="Use POA verbiage"
          name="poaVerbiage"
          value={form.poaVerbiage}
          onChange={handleChange}
        />
      )}

      {isStringType && (
        <CheckboxCDS
          fullWidth
          dataTestId={formsTestIds.STYLE_OPTIONS_UPPERCASE_CHECKBOX}
          label="Uppercase"
          name="isUppercase"
          value={form.isUppercase}
          onChange={handleChange}
        />
      )}

      <DynamicFieldBoldCheckbox
        isHidden={!isBoldInFirstRow}
        value={form.isBold}
        onChange={handleChange}
      />

      {isBoldInFirstRow && !hasLeadingCheckbox && <Box />}

      {isAvailableSpelledOut && (
        <>
          <CheckboxCDS
            fullWidth
            dataTestId={formsTestIds.STYLE_OPTIONS_SPELLED_OUT_CHECKBOX}
            label="Spelled Out"
            name="isSpelledOut"
            value={form.isSpelledOut}
            onChange={handleChange}
          />
          <Box />
        </>
      )}

      <AutocompleteCDS
        disableClearable
        dataTestId={formsTestIds.STYLE_OPTIONS_PREFIX_SELECT}
        isHidden={!hasDollarPrefix}
        label="Prefix"
        name="prefix"
        options={dynamicFieldPrefixOptions}
        value={form.prefix}
        onChange={onAutocompleteChange}
      />

      <AutocompleteCDS
        disableClearable
        dataTestId={formsTestIds.STYLE_OPTIONS_POSTFIX_SELECT}
        isHidden={!hasPercentPostfix}
        label="Postfix"
        name="postfix"
        options={dynamicFieldPostfixOptions}
        value={form.postfix}
        onChange={onAutocompleteChange}
      />

      {isNumericWithDecimalPositions && (
        <NumberFormat
          fullWidth
          allowNegative={false}
          customInput={TextFieldCDS}
          decimalScale={0}
          isAllowed={isDecimalPositionsAllowed}
          label="Decimal Positions"
          name="decimalPositions"
          placeholder={String(getFieldDecimalPositionsDefault(schemaDefaultAction, type))}
          type="text"
          value={form.decimalPositions}
          variant="outlined"
          onBlur={onDecimalPositionsBlur}
          onChange={handleChange}
        />
      )}

      {isNumericWithThousandsSeparator && (
        <CheckboxCDS
          fullWidth
          dataTestId={formsTestIds.STYLE_OPTIONS_THOUSANDS_SEPARATOR_CHECKBOX}
          label="Thousands separator"
          name="withThousandsSeparator"
          value={form.withThousandsSeparator}
          onChange={handleChange}
        />
      )}

      {isMultiplierVisible && (
        <NumberFormat
          fullWidth
          allowNegative={false}
          customInput={TextFieldCDS}
          decimalScale={0}
          isAllowed={isMultiplierAllowed}
          label="Multiplier"
          name="multiplier"
          placeholder={String(DYNAMIC_FIELD_DEFAULT_MULTIPLIER)}
          type="text"
          value={form.multiplier}
          variant="outlined"
          onBlur={onMultiplierBlur}
          onChange={handleChange}
        />
      )}

      <AutocompleteCDS
        disablePortal
        required
        columns={2}
        dataTestId={formsTestIds.STYLE_OPTIONS_TAG_SELECT}
        error={!!validationErrors.tag}
        helperText={validationErrors.tag}
        isHidden={!isTag}
        isOptionEqualToValue={onGetTagOptionSelected}
        label="Tag"
        name="tag"
        options={tags}
        type="text"
        value={form.tag || null}
        onChange={onAutocompleteChange}
      />

      {isImageType && (
        <>
          <NumberFormat
            fullWidth
            allowNegative={false}
            customInput={TextFieldCDS}
            decimalScale={0}
            disabled={isPdfForm}
            label="Size"
            name="size"
            suffix="%"
            type="text"
            value={form.size}
            variant="outlined"
            onChange={handleChange}
          />

          <AutocompleteCDS
            dataTestId={formsTestIds.STYLE_OPTIONS_FLOAT_SELECT}
            disabled={isPdfForm}
            label="Float"
            name="float"
            options={floatOptions}
            value={form.float}
            onChange={onAutocompleteChange}
          />
        </>
      )}

      {showStyleSection && (
        <StyleViewOptions
          backgroundColor={form.backgroundColor}
          checkboxRenderType={form.checkboxRenderType}
          defaultFontFamilyOption={defaultFontFamilyOption}
          fontColor={form.fontColor}
          fontFamily={form.fontFamily}
          fontFamilyOptions={fontFamilyOptions}
          fontSize={form.fontSize}
          handleChange={handleChange}
          horizontalAlignment={form.horizontalAlignment}
          isAlignmentHidden={isBlankSignatureLine}
          isBackgroundColorEnabled={isBackgroundColorAvailable}
          isBold={form.isBold}
          setInForm={setInForm}
          validateField={validateField}
          verticalAlignment={form.verticalAlignment}
        />
      )}
    </DynamicFieldOptionsLayout>
  );
};

DynamicFieldStyleOptionsView.propTypes = {
  isAvailablePostfix: PropTypes.bool.isRequired,
  isAvailablePrefix: PropTypes.bool.isRequired,
  chosenOption: PropTypes.array.isRequired,
  editor: PropTypes.object.isRequired,
  style: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  setParentView: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  action: PropTypes.object,
  isAvailableSpelledOut: PropTypes.bool,
  checkboxRenderType: PropTypes.string,
  dataDynamicMultiplier: PropTypes.number,
  schemaDefaultAction: PropTypes.object,
  dataDynamicPostfix: PropTypes.string,
  dataDynamicPrefix: PropTypes.string,
  withLabel: PropTypes.bool,
  poaVerbiage: PropTypes.bool,
  fontFamilyOptions: PropTypes.arrayOf(PropTypes.object),
  defaultFontFamilyOption: PropTypes.object,
  isNewItem: PropTypes.bool,
};

export default DynamicFieldStyleOptionsView;
